import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Plus, Wrench, Eye, Trash2, Clock, CheckCircle, XCircle, AlertTriangle, PlayCircle,
  ChevronDown, ChevronRight, FolderOpen, Folder, Archive, Zap, Search, RefreshCw
} from 'lucide-react';
import api from '../../api.js';
import { useActiveVehicles, useActiveDrivers } from '../../hooks/useDataHooks.js';
import { useAccordionState } from '../../hooks/useAccordionState.js';
import MaintenanceFormModal from './MaintenanceFormModal.jsx';
import MaintenanceDetailModal from './MaintenanceDetailModal.jsx';
import { useSearch } from '../../v3/context/SearchContext.jsx';
import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';
import { useFormat } from '../../i18n/format.js';
import SkeletonLoader from '../common/SkeletonLoader.jsx';

// Status configuration — 3-step lean workflow (2026-04-21). Legacy values
// fold onto the nearest canonical state so pre-migration rows still render.
const STATUS_CONFIG = {
  // Canonical
  created:        { color: 'bg-blue-100 text-blue-800',     label: 'Créée',     icon: Clock,       group: 'current' },
  planned:        { color: 'bg-amber-100 text-amber-800',   label: 'Planifiée', icon: Clock,       group: 'current' },
  completed:      { color: 'bg-emerald-100 text-emerald-800', label: 'Terminée', icon: CheckCircle, group: 'done' },
  cancelled:      { color: 'bg-gray-100 text-gray-500',     label: 'Annulée',   icon: XCircle,     group: 'done' },
  // Legacy (pre-migration rows)
  draft:          { color: 'bg-blue-100 text-blue-800',     label: 'Créée',     icon: Clock,       group: 'current' },
  submitted:      { color: 'bg-blue-100 text-blue-800',     label: 'Créée',     icon: Clock,       group: 'current' },
  manager_review: { color: 'bg-blue-100 text-blue-800',     label: 'Créée',     icon: Clock,       group: 'current' },
  approved:       { color: 'bg-blue-100 text-blue-800',     label: 'Créée',     icon: Clock,       group: 'current' },
  scheduled:      { color: 'bg-amber-100 text-amber-800',   label: 'Planifiée', icon: Clock,       group: 'current' },
  in_progress:    { color: 'bg-amber-100 text-amber-800',   label: 'Planifiée', icon: Clock,       group: 'current' },
  rejected:       { color: 'bg-gray-100 text-gray-500',     label: 'Annulée',   icon: XCircle,     group: 'done' }
};

// Status order for display (canonical first, legacy kept for backwards render)
const STATUS_ORDER = {
  current: ['created', 'planned', 'draft', 'submitted', 'manager_review', 'approved', 'scheduled', 'in_progress'],
  done: ['completed', 'cancelled', 'rejected']
};

// Priority configuration
const PRIORITY_CONFIG = {
  low: { color: 'bg-gray-100 text-gray-600', label: 'Basse' },
  medium: { color: 'bg-blue-100 text-blue-700', label: 'Moyenne' },
  high: { color: 'bg-orange-100 text-orange-700', label: 'Haute' },
  critical: { color: 'bg-red-100 text-red-700', label: 'Critique' }
};

// Type labels
const TYPE_LABELS = {
  oil_change: 'Vidange',
  tire_rotation: 'Rotation pneus',
  brake_service: 'Freins',
  engine_repair: 'Moteur',
  transmission: 'Transmission',
  electrical: 'Électrique',
  bodywork: 'Carrosserie',
  inspection: 'Inspection',
  emergency: 'Urgence',
  other: 'Autre'
};

function MaintenanceView({ onMessage, autoSelectId, createForVehicleId, returnTo, onNavigate }) {
  const [reqLoading, setReqLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const { data: vehicles = [], isLoading: vehiclesLoading } = useActiveVehicles();
  const { data: drivers = [], isLoading: driversLoading } = useActiveDrivers();
  const [showForm, setShowForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const { searchQuery } = useSearch();
  const { t, fmt } = useFormat();
  // Devise : celle de l'org de la demande ; total converti dans la devise du périmètre.
  const { money, sumScope } = useOrgCurrency();
  const autoSelectProcessed = useRef(false);
  const createForVehicleProcessed = useRef(false);

  // Handle closing detail modal - navigate back if returnTo is set
  const handleCloseDetailModal = () => {
    setSelectedRequest(null);
    if (returnTo && onNavigate) {
      onNavigate(returnTo);
    }
  };

  // Accordion state - persisted to DB
  const {
    isExpanded,
    toggleGroup: toggleAccordion,
  } = useAccordionState({ viewCode: 'maintenance' });

  // Toggle wrappers with prefixed keys
  const toggleGroup = (group) => toggleAccordion(`group:${group}`);
  const toggleStatus = (status) => toggleAccordion(`status:${status}`);

  const loading = reqLoading || vehiclesLoading || driversLoading;

  const loadData = async () => {
    try {
      setReqLoading(true);
      const reqRes = await api.maintenance.getAll();
      setRequests(reqRes.data || []);
    } catch (error) {
      console.error('Error loading maintenance requests:', error);
      onMessage?.({
        title: t('maintenance.messages.errorTitle'),
        message: t('maintenance.messages.loadError'),
        type: 'error'
      });
    } finally {
      setReqLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select maintenance request when navigating from another view
  useEffect(() => {
    if (autoSelectId && requests.length > 0 && !autoSelectProcessed.current) {
      const requestToSelect = requests.find(r => r.id === autoSelectId);
      if (requestToSelect) {
        setSelectedRequest(requestToSelect);
        autoSelectProcessed.current = true;
      }
    }
  }, [autoSelectId, requests]);

  useEffect(() => {
    if (autoSelectId) {
      autoSelectProcessed.current = false;
    }
  }, [autoSelectId]);

  // Auto-open creation form for a specific vehicle (from Vehicles action menu)
  useEffect(() => {
    if (createForVehicleId && !createForVehicleProcessed.current) {
      createForVehicleProcessed.current = true;
      setShowForm(true);
    }
  }, [createForVehicleId]);

  // Filter requests by search
  const filteredRequests = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return requests;
    const query = searchQuery.toLowerCase();
    return requests.filter(request => {
      const vehicle = vehicles.find(v => v.id === request.vehicle_id);
      return (
        request.title?.toLowerCase().includes(query) ||
        request.description?.toLowerCase().includes(query) ||
        vehicle?.license_plate?.toLowerCase().includes(query) ||
        vehicle?.make?.toLowerCase().includes(query) ||
        vehicle?.model?.toLowerCase().includes(query) ||
        (TYPE_LABELS[request.type]
          ? t(`maintenance.types.${request.type}`, { defaultValue: TYPE_LABELS[request.type] }).toLowerCase().includes(query)
          : false) ||
        (STATUS_CONFIG[request.status]
          ? t(`maintenance.status.${request.status}`, { defaultValue: STATUS_CONFIG[request.status].label }).toLowerCase().includes(query)
          : false)
      );
    });
  }, [requests, searchQuery, vehicles, t]);

  // Group requests by current/done and then by status
  const groupedRequests = useMemo(() => {
    const groups = {
      current: {
        label: t('maintenance.groups.current'),
        icon: Zap,
        statuses: {},
        totalCount: 0
      },
      done: {
        label: t('maintenance.groups.done'),
        icon: Archive,
        statuses: {},
        totalCount: 0
      }
    };

    // Initialize status groups
    STATUS_ORDER.current.forEach(status => {
      groups.current.statuses[status] = [];
    });
    STATUS_ORDER.done.forEach(status => {
      groups.done.statuses[status] = [];
    });

    // Sort filtered requests into groups
    filteredRequests.forEach(request => {
      const statusConfig = STATUS_CONFIG[request.status];
      if (statusConfig) {
        const groupKey = statusConfig.group;
        groups[groupKey].statuses[request.status].push(request);
        groups[groupKey].totalCount++;
      }
    });

    return groups;
  }, [filteredRequests, t]);

  // Stats
  const stats = useMemo(() => {
    const currentCount = requests.filter(r => STATUS_CONFIG[r.status]?.group === 'current').length;
    const doneCount = requests.filter(r => STATUS_CONFIG[r.status]?.group === 'done').length;
    const criticalCount = requests.filter(r => r.priority === 'critical' && STATUS_CONFIG[r.status]?.group === 'current').length;
    const highCount = requests.filter(r => r.priority === 'high' && STATUS_CONFIG[r.status]?.group === 'current').length;
    // Prefer the actual invoiced amount over the running total/estimate so
    // completed jobs reflect what was really billed (REG051).
    // Somme MULTI-DEVISES : conversion dans la devise du perimetre avant addition.
    const totalCost = sumScope(
      requests.filter(r => r.status === 'completed'),
      r => r.actual_total ?? r.total_cost ?? r.estimated_cost ?? 0
    );

    return {
      total: requests.length,
      current: currentCount,
      done: doneCount,
      critical: criticalCount,
      high: highCount,
      totalCost
    };
  }, [requests, sumScope]);

  const handleFormSuccess = () => {
    loadData();
    setShowForm(false);
    // Return to origin view (e.g. vehicles) after successful submission
    if (returnTo && onNavigate) {
      onNavigate(returnTo);
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm(t('maintenance.list.deleteConfirm'))) return;

    try {
      await api.maintenance.delete(requestId);
      onMessage?.({
        title: t('maintenance.messages.successTitle'),
        message: t('maintenance.messages.deleted'),
        type: 'success'
      });
      loadData();
    } catch (error) {
      onMessage?.({
        title: t('maintenance.messages.errorTitle'),
        message: t('maintenance.messages.deleteError'),
        type: 'error'
      });
    }
  };

  // Get vehicle info
  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? `${vehicle.make} ${vehicle.model} - ${vehicle.license_plate}` : t('maintenance.list.vehicleFallback', { id: vehicleId });
  };

  // Get driver info
  const _getDriverInfo = (driverId) => {
    if (!driverId) return '-';
    const driver = drivers.find(d => d.id === driverId);
    return driver ? `${driver.first_name} ${driver.last_name}` : t('maintenance.list.driverFallback', { id: driverId });
  };

  // Loading state
  if (loading) {
    return <SkeletonLoader type="list" items={6} />;
  }

  return (
    <div className="space-y-4">
      {/* Form Modal */}
      <MaintenanceFormModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSuccess={handleFormSuccess}
        onMessage={onMessage}
        vehicles={vehicles}
        drivers={drivers}
        defaultVehicleId={createForVehicleId}
      />

      {/* Detail Modal */}
      {selectedRequest && (
        <MaintenanceDetailModal
          request={selectedRequest}
          onClose={handleCloseDetailModal}
          onUpdate={loadData}
          onMessage={onMessage}
          vehicles={vehicles}
          drivers={drivers}
        />
      )}

      {/* Header Card with Stats and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Wrench className="w-6 h-6 text-orange-600" />
              {t('maintenance.list.title')}
            </h2>
            <p className="text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">{t('maintenance.list.requestCount', { count: stats.total })}</span>
              <span className="text-gray-300">|</span>
              <span className="text-purple-600">{t('maintenance.list.currentCount', { count: stats.current })}</span>
              {stats.critical > 0 && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-red-600 font-semibold">{t('maintenance.list.criticalCount', { count: stats.critical })}</span>
                </>
              )}
              {stats.high > 0 && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-orange-600">{t('maintenance.list.highCount', { count: stats.high })}</span>
                </>
              )}
              <span className="text-gray-300">|</span>
              <span className="text-green-600">{t('maintenance.list.doneCount', { count: stats.done })}</span>
              {stats.totalCost.value > 0 && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-blue-600">
                    {stats.totalCost.label}
                  </span>
                </>
              )}
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2 shadow-sm"
          >
            <Plus className="w-5 h-5" />
            {t('maintenance.list.newRequest')}
          </button>
        </div>

        {/* Refresh */}
        <div className="flex gap-4 justify-end">
          <button
            onClick={loadData}
            disabled={loading}
            className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg border border-gray-300 transition-colors"
            title={t('common.actions.refresh')}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Accordion Groups */}
      <div className="space-y-3">
        {['current', 'done'].map((groupKey) => {
          const group = groupedRequests[groupKey];
          const isGroupExpanded = isExpanded(`group:${groupKey}`);
          const GroupIcon = group.icon;
          const statusList = STATUS_ORDER[groupKey];
          const nonEmptyStatuses = statusList.filter(s => group.statuses[s].length > 0);

          return (
            <div
              key={groupKey}
              className={`border rounded-lg overflow-hidden transition-all ${
                isGroupExpanded ? 'border-blue-300 shadow-sm' : 'border-gray-200'
              }`}
            >
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(groupKey)}
                className={`w-full flex items-center justify-between p-4 text-left transition-colors ${
                  isGroupExpanded
                    ? 'bg-blue-50 hover:bg-blue-100'
                    : 'bg-white hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  {isGroupExpanded ? (
                    <FolderOpen className="w-5 h-5 text-blue-600" />
                  ) : (
                    <Folder className="w-5 h-5 text-gray-400" />
                  )}
                  <GroupIcon className={`w-5 h-5 ${
                    groupKey === 'current' ? 'text-purple-500' : 'text-green-500'
                  }`} />
                  <span className={`text-lg font-semibold ${
                    isGroupExpanded ? 'text-blue-900' : 'text-gray-900'
                  }`}>
                    {group.label}
                  </span>
                  {groupKey === 'current' && group.totalCount > 0 && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full animate-pulse">
                      {t('maintenance.groups.activeBadge')}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-4">
                  {!isGroupExpanded && (
                    <span className="text-sm text-gray-500">
                      {t('maintenance.list.requestCount', { count: group.totalCount })} • {t('maintenance.list.statusCount', { count: nonEmptyStatuses.length })}
                    </span>
                  )}
                  <span className={`px-2.5 py-1 text-sm font-medium rounded-full ${
                    isGroupExpanded ? 'bg-blue-200 text-blue-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {group.totalCount}
                  </span>
                  {isGroupExpanded ? (
                    <ChevronDown className="w-5 h-5 text-blue-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {/* Group Content - Status Accordions */}
              {isGroupExpanded && (
                <div className="border-t border-blue-200 bg-gray-50">
                  {/* Status List */}
                  <div className="divide-y divide-gray-200">
                    {statusList.map((status) => {
                      const statusRequests = group.statuses[status];
                      const statusConfig = STATUS_CONFIG[status];
                      const StatusIcon = statusConfig.icon;
                      const isStatusExpanded = isExpanded(`status:${status}`);

                      if (statusRequests.length === 0) return null;

                      return (
                        <div key={status} className="bg-white">
                          {/* Status Header */}
                          <button
                            onClick={() => toggleStatus(status)}
                            className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                              isStatusExpanded
                                ? 'bg-gray-50 border-l-4 border-blue-500'
                                : 'hover:bg-gray-50 border-l-4 border-transparent'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {isStatusExpanded ? (
                                <ChevronDown className="w-4 h-4 text-blue-600" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              )}
                              <StatusIcon className={`w-4 h-4 ${
                                statusConfig.color.includes('gray') ? 'text-gray-500' :
                                statusConfig.color.includes('blue') ? 'text-blue-500' :
                                statusConfig.color.includes('yellow') ? 'text-yellow-500' :
                                statusConfig.color.includes('green') ? 'text-green-500' :
                                statusConfig.color.includes('emerald') ? 'text-emerald-500' :
                                statusConfig.color.includes('purple') ? 'text-purple-500' :
                                statusConfig.color.includes('red') ? 'text-red-500' :
                                'text-gray-500'
                              }`} />
                              <span className={`font-medium ${
                                isStatusExpanded ? 'text-blue-900' : 'text-gray-700'
                              }`}>
                                {t(`maintenance.status.${status}`, { defaultValue: statusConfig.label })}
                              </span>
                            </div>

                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.color}`}>
                              {t('maintenance.list.requestCount', { count: statusRequests.length })}
                            </span>
                          </button>

                          {/* Status Content - Request List */}
                          {isStatusExpanded && (
                            <div className="px-4 py-3 space-y-2 bg-gray-50 border-l-4 border-blue-500">
                              {statusRequests.map(request => {
                                const priorityConfig = PRIORITY_CONFIG[request.priority] || PRIORITY_CONFIG.medium;

                                return (
                                  <div
                                    key={request.id}
                                    className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                          <h4 className="font-medium text-gray-900 truncate">
                                            {request.title}
                                          </h4>
                                          <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${priorityConfig.color}`}>
                                            {t(`maintenance.priority.${request.priority || 'medium'}`, { defaultValue: priorityConfig.label })}
                                          </span>
                                        </div>
                                        <div className="text-sm text-gray-600 mb-2">
                                          {getVehicleInfo(request.vehicle_id)}
                                        </div>
                                        <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                                          <span>{TYPE_LABELS[request.type] ? t(`maintenance.types.${request.type}`, { defaultValue: TYPE_LABELS[request.type] }) : request.type}</span>
                                          {request.garage_name && (
                                            <span className="text-amber-700">🔧 {request.garage_name}</span>
                                          )}
                                          {request.scheduled_date && (
                                            <span>{t('maintenance.list.appointment', { date: fmt.date(request.scheduled_date) })}</span>
                                          )}
                                          {request.quote_total > 0 && (
                                            <span>
                                              {t('maintenance.list.quote', { amount: money(request.quote_total, request) })}
                                            </span>
                                          )}
                                          {request.actual_total > 0 && request.status === 'completed' && (
                                            <span className="text-green-600 font-medium">
                                              {t('maintenance.list.invoice', { amount: money(request.actual_total, request) })}
                                            </span>
                                          )}
                                          {!request.quote_total && request.estimated_cost > 0 && (
                                            <span>
                                              {t('maintenance.list.estimate', { amount: money(request.estimated_cost, request) })}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2 ml-4">
                                        <button
                                          onClick={() => setSelectedRequest(request)}
                                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                          title={t('maintenance.list.viewDetails')}
                                        >
                                          <Eye className="w-4 h-4" />
                                        </button>
                                        {['created', 'draft', 'submitted'].includes(request.status) && (
                                          <button
                                            onClick={() => handleDelete(request.id)}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                            title={t('common.actions.delete')}
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Empty state for group */}
                    {nonEmptyStatuses.length === 0 && (
                      <div className="py-8 text-center text-gray-500">
                        <Wrench className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>{groupKey === 'current' ? t('maintenance.list.emptyGroupCurrent') : t('maintenance.list.emptyGroupDone')}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Global Empty State */}
      {filteredRequests.length === 0 && (
        <div className="bg-white p-12 rounded-lg shadow-md text-center">
          <Wrench className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {searchQuery ? t('common.states.noResult') : t('maintenance.list.emptyTitle')}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchQuery
              ? t('maintenance.list.emptySearchHint')
              : t('maintenance.list.emptyHint')}
          </p>
          {!searchQuery && (
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {t('maintenance.list.newRequest')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default MaintenanceView;
